import { inject, singleton } from "tsyringe";
import { expiryDate, TimeUnit, TopLevelDomainExtractor } from "@identity/utils";
import { BeaconGenerator } from "../BeaconGenerator";
import { COOKIES, WINDOW } from "@identity/symbols";

@singleton()
export class TrackingCookiesService {
  constructor(
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(BeaconGenerator) private beaconGenerator: BeaconGenerator,
    @inject(WINDOW)
    private readonly window: Window,
  ) {}

  public setPageViewCookies() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    const { pvNumber, pvNumberGlobal } = this.window.fandomContext.tracking;

    if (pvNumber) {
      this.cookiesManager.set("pvNumber", pvNumber.toString(), {
        domain: domain,
        expires: expiryDate(30, TimeUnit.MINUTES),
      });
    }

    if (pvNumberGlobal) {
      this.cookiesManager.set("pvNumberGlobal", pvNumberGlobal.toString(), {
        domain: topLevelDomain,
        expires: expiryDate(30, TimeUnit.MINUTES),
      });
    }
  }

  private setUserTrackingCookies() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);

    const { beaconId, sessionId } = this.window.fandomContext.tracking;

    if (beaconId) {
      this.cookiesManager.set("wikia_beacon_id", beaconId, {
        domain: topLevelDomain,
        expires: expiryDate(180, TimeUnit.DAYS),
      });
    }

    const b2 = this.cookiesManager.get("_b2");
    this.cookiesManager.set("_b2", b2 || this.beaconGenerator.generateV2(), {
      domain: topLevelDomain,
      expires: expiryDate(730, TimeUnit.DAYS),
    });

    if (sessionId) {
      this.cookiesManager.set("sessionId", sessionId, {
        domain: topLevelDomain,
        expires: expiryDate(30, TimeUnit.MINUTES),
      });
    }

    // This cookie was set by TrackingOptIn for 180 days with a beacon-like value.
    const wikiaSessionId = this.cookiesManager.get("wikia_session_id");
    this.cookiesManager.set(
      "wikia_session_id",
      wikiaSessionId || this.beaconGenerator.generate(),
      { domain: topLevelDomain, expires: expiryDate(180, TimeUnit.DAYS) },
    );
  }

  public setTrackingCookies(): void {
    this.setUserTrackingCookies();
    this.setPageViewCookies();
  }
}
